import { useCallback, useContext, useEffect } from "react"
import { useHistory, useLocation } from "react-router-dom"
import {
  atom,
  selector,
  useRecoilState,
  useRecoilValue,
  useSetRecoilState
} from "recoil"
import { Topic } from "src/components/views/journey"
import { Routes, Views } from "src/constants"
import { SDKContext } from "src/context/SDKContext"
import { useValidateRoom } from "src/hooks/useValidateRoom"
import { fillColor } from "../../../components/journey/tile"
import { subTopicSelector, topicSelector } from "../lessons"
import { viewSelector } from "../view"

type Pageload = {
  transition: "pageload"
  route?: "topics" | "subtopics"
  id?: number
}

type UiStates =
  | Pageload
  | {
      transition: "topicsToSubtopics"
      id: number
    }
  | {
      transition: "subtopicsToLessons"
      externalRoute: `/${string}`
    }
  | {
      transition: "subtopicsToTopics"
    }
  | {
      transition: "lessonsToSubtopics"
    }
  | {
      transition: "externalToJourney"
    }
  | { transition: "journeyToExternal"; externalRoute: `/${string}` }

const defaultState: UiStates = { transition: "pageload" }

const journeyUiState = atom<UiStates>({
  key: "journey-ui-state",
  default: defaultState
})

export const journeyUiHistoryState = atom<UiStates[]>({
  key: "journey-ui-history-state",
  default: []
})

export const journeyUiSelector = selector<UiStates>({
  key: "journey-ui-state-selector",
  get: ({ get }) => {
    return get(journeyUiState)
  },
  set: ({ set, get }, newProps) => {
    if (newProps === null) {
      set(journeyUiState, null)
    } else {
      set(journeyUiHistoryState, [
        ...get(journeyUiHistoryState),
        { ...get(journeyUiState) }
      ])
      set(journeyUiState, { ...newProps })
    }
  }
})

export function isRoomHashPath(pathname: string) {
  return !!pathname.match(/[^\/]+-[^\/]+-[^\/]+/)
}

export function isSubTopicPage(pathname: string) {
  return !!pathname.match(/\/journey\/\d+/)
}

export const useJourneyPageTransition = () => {
  const [state, setJourneyUi] = useRecoilState(journeyUiSelector)
  const [activeTopic, setActiveTopic] = useRecoilState(topicSelector)
  const setActiveSubtopic = useSetRecoilState(topicSelector)
  const topics = useRecoilValue(topicsAndSubtopics)
  const location = useLocation()
  const history = useHistory()
  const setView = useSetRecoilState(viewSelector)
  const meetingClient = useContext(SDKContext)
  const { validateRoom } = useValidateRoom(meetingClient)

  const pageload = usePageloadAction()

  useEffect(() => {
    if (!topics?.length) return
    switch (state.transition) {
      case "pageload": {
        pageload()
        break
      }
      case "subtopicsToLessons": {
        history.push(state.externalRoute)
        meetingClient.setRoomHash(state.externalRoute.slice(1))
        setView(Views.LOADING)
        validateRoom()
        break
      }
      case "topicsToSubtopics": {
        if (isSubTopicPage(location.pathname)) return
        history.push(`${Routes.JOURNEY}/${state.id}`)
        break
      }
      case "subtopicsToTopics": {
        setActiveTopic(null)
        setActiveSubtopic(null)
        history.push(Routes.JOURNEY)
        break
      }
      case "journeyToExternal": {
        history.push(state.externalRoute)
        break
      }
      case "externalToJourney": {
        if (activeTopic?.id) {
          history.push(`${Routes.JOURNEY}/${activeTopic.id}`)
        } else {
          history.push(Routes.JOURNEY)
        }
        break
      }
    }
  }, [state, topics])

  return { setJourneyUi }
}

export const topicsAndSubtopics = atom<Topic[]>({
  key: "journey-topics-and-subtopics",
  default: null
})

/**
 * @description You must use this hook within any page view
 * that is linked to from the /journey route so that the browser
 * back button is functional.
 */
export function useReturnToJourneyAction() {
  const state = useRecoilValue(journeyUiSelector)
  const activeTopic = useRecoilValue(topicSelector)
  const history = useHistory()
  const pageload = usePageloadAction()

  useEffect(() => {
    pageload()
  }, [state])

  useEffect(() => {
    if (state.transition === "externalToJourney") {
      if (activeTopic && activeTopic.id) {
        history.push(`${Routes.JOURNEY}/${activeTopic.id}`)
      } else {
        history.push(Routes.JOURNEY)
      }
    }
  }, [state])
}

function usePageloadAction() {
  const state = useRecoilValue(journeyUiSelector)
  const setActiveTopic = useSetRecoilState(topicSelector)
  const setActiveSubtopic = useSetRecoilState(subTopicSelector)
  const topics = useRecoilValue(topicsAndSubtopics)
  const history = useHistory()
  const location = useLocation()

  function isPageloadUiAction(state): state is Required<Pageload> {
    return state && state.transition === "pageload" && state.id
  }

  const action = () => {
    let mutableState = {...state}
    if (mutableState.transition === "pageload" && !mutableState.id) {
      mutableState.id = parseInt(location.pathname.split("/").at(-1) || "0")
    }

    if (isPageloadUiAction(mutableState)) {
      const id = mutableState.id
      const topic = topics?.find(topic => topic.id === id)

      if (topic) {
        setActiveSubtopic(null)
        setActiveTopic({
          id: topic.id,
          centerPos: {
            x: window.innerWidth / 2,
            y: window.innerHeight / 2
          },
          color: fillColor(topic.progress.new / 100 / 360),
          name: topic.name
        })
        history.push(`${Routes.JOURNEY}/${mutableState.id}`)
      } else if (state && state.transition === "pageload") {
        setActiveTopic(null)
        setActiveSubtopic(null)
        history.replace(Routes.JOURNEY)
      }
    }
  }
  return action
}